import { BookingWaiverRemovalEntry, WaiverBookingType } from '@types';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';

type WaiverRemovalLogModalProps = {
  isOpen: boolean;
  bookingType: Exclude<WaiverBookingType, 'skating_session'>;
  recordId: number | null;
  onClose: () => void;
  onViewWaiver: (url: string) => void;
};

export const WaiverRemovalLogModal: React.FC<WaiverRemovalLogModalProps> = ({
  isOpen,
  bookingType,
  recordId,
  onClose,
  onViewWaiver,
}) => {
  const [entries, setEntries] = useState<BookingWaiverRemovalEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !recordId) return;

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = (await AdminService.getWaiverRemovals(
          bookingType,
          recordId,
        )) as unknown as BookingWaiverRemovalEntry[];
        if (!cancelled) {
          setEntries(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error loading waiver removal log:', error);
        if (!cancelled) setEntries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, bookingType, recordId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Waiver Log</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {loading ? (
            <div className="text-center text-gray-500 py-4 text-sm">
              Loading waiver log...
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center text-gray-500 py-4 text-sm">
              No waivers have been removed from this booking.
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map(entry => (
                <div
                  key={entry.id}
                  className="rounded-lg p-4 border bg-amber-50 border-amber-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                      Waiver Removed
                    </span>
                    <span className="text-xs text-gray-500">
                      {HelperMethods.formatDateTimeLocal(entry.removedAt)}
                    </span>
                  </div>
                  {entry.holderName && (
                    <div className="text-sm text-gray-900">
                      Holder: {entry.holderName}
                    </div>
                  )}
                  <div className="text-sm text-gray-900">
                    {entry.previousWaiverType === 'Paper'
                      ? 'Paper waiver removed'
                      : 'Digital waiver removed'}
                  </div>
                  {entry.previousWaiverUrl && (
                    <button
                      type="button"
                      onClick={() => onViewWaiver(entry.previousWaiverUrl!)}
                      className="mt-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      View previous waiver
                    </button>
                  )}
                  <div className="text-xs text-gray-600 mt-2 space-y-0.5">
                    <div>
                      Requested by: {entry.requestedByType}
                      {entry.requestedByNotes && ` — ${entry.requestedByNotes}`}
                    </div>
                    <div>
                      Processed by: {entry.processedByName || 'Staff member'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
